import { Controller } from "@hotwired/stimulus"

/**
 * Appointment Form Controller
 *
 * Handles all form-related functionality for appointments:
 * - Time validation and restrictions
 * - Location handling (Virtual vs Presencial)
 * - Date validation
 * - Form validation
 * - File handling
 * - Collapsible sections
 * - Price handling
 *
 * This controller is designed to be used alongside the main appointments_controller.js
 * for better separation of concerns.
 */
export default class extends Controller {
  static targets = [
    "addressField",
    "meetLinkField",
    "meetLinkInput",
    "dateField",
    "outOfOfficeDates",
    "startTime",
    "endTime",
    "startHour",
    "startMinute",
    "endHour",
    "endMinute",
    "headerDateTime",
    "titleField",
    "moreInfoIcon",
    "paymentBillingIcon",
    "calculatedPriceDisplay",
    "patientId"
  ]

  connect() {
    this.abortController = new AbortController()
    this.initializeForm()
  }

  disconnect() {
    // Abort any pending fetch requests
    if (this.abortController) {
      this.abortController.abort()
    }
  }

  initializeForm() {
    // Initialize price handling
    this.initializePriceHandling()

    // Initialize location if we're in an appointment form
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')
    if (locationInput) {
      this.updateLocation({ target: { dataset: { location: locationInput.value } } })
    }

    // Initialize time restrictions if in appointment form
    if (this.hasStartHourTarget && this.hasEndHourTarget &&
        this.hasStartMinuteTarget && this.hasEndMinuteTarget &&
        this.hasStartTimeTarget && this.hasEndTimeTarget) {
      this.initializeTimeRestrictions()
      this.validateStartTime()
      this.validateEndTime()
      this.adjustInitialEndTime()
    }

    // Initialize address validation
    if (this.hasAddressFieldTarget) {
      this.validateAddress()
    }

    // Update header date/time if in appointment form
    if (this.hasDateFieldTarget && this.hasStartHourTarget &&
        this.hasStartMinuteTarget && this.hasHeaderDateTimeTarget) {
      this.updateHeaderDateTime()
    }

    // Initialize date picker validation
    this.initializeDatePickerValidation()

    // Initialize collapsible section icons
    this.initializeCollapsibleIcons()

    // Initialize time selectors
    this.initializeTimeSelectors()
  }

  // ==============================================
  // TIME HANDLING
  // ==============================================

  initializeTimeSelectors() {
    const startHourSelect = document.querySelector('[data-appointment-form-target="startHour"]')
    const endHourSelect = document.querySelector('[data-appointment-form-target="endHour"]')

    if (startHourSelect) {
      const startEvent = new Event('change')
      startHourSelect.dispatchEvent(startEvent)
    }

    if (endHourSelect) {
      const endEvent = new Event('change')
      endHourSelect.dispatchEvent(endEvent)
    }
  }

  initializeTimeRestrictions() {
    // Save original time in edit mode
    if (this.hasStartHourTarget && this.hasStartMinuteTarget) {
      const startHour = this.startHourTarget.value
      const startMinute = this.startMinuteTarget.value
      this.startTimeTarget.dataset.originalTime = `${startHour}:${startMinute}`
    }

    if (this.hasEndHourTarget && this.hasEndMinuteTarget) {
      const endHour = this.endHourTarget.value
      const endMinute = this.endMinuteTarget.value
      this.endTimeTarget.dataset.originalTime = `${endHour}:${endMinute}`
    }

    this.updateHiddenTimeFields()
  }

  updateHiddenTimeFields() {
    if (this.hasStartHourTarget && this.hasStartMinuteTarget) {
      const startHour = this.startHourTarget.value
      const startMinute = this.startMinuteTarget.value
      this.startTimeTarget.value = `${startHour}:${startMinute}`
    }

    if (this.hasEndHourTarget && this.hasEndMinuteTarget) {
      const endHour = this.endHourTarget.value
      const endMinute = this.endMinuteTarget.value
      this.endTimeTarget.value = `${endHour}:${endMinute}`
    }
  }

  validateStartTime() {
    if (!this.hasStartTimeTarget || !this.hasEndTimeTarget) return

    this.updateHiddenTimeFields()
    this.updateHeaderDateTime()

    // Only auto-adjust end time if NOT in edit mode
    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null

    if (!isEditMode) {
      // Auto-adjust end time based on patient duration
      const patientSearchController = this.application.getControllerForElementAndIdentifier(
        this.element.querySelector('[data-controller*="patient-search"]'),
        'patient-search'
      )
      let durationMinutes = 30
      if (patientSearchController) {
        const patientId = patientSearchController.hiddenInputTarget.value
        if (patientId) {
          const firstAppointmentBadge = patientSearchController.firstAppointmentBadgeTarget
          const isFirstAppointment = firstAppointmentBadge && !firstAppointmentBadge.classList.contains('d-none')
          durationMinutes = isFirstAppointment ? 45 : 30
        }
      }
      this.adjustEndTime(durationMinutes)
    }
  }

  validateEndTime() {
    if (!this.hasStartTimeTarget || !this.hasEndTimeTarget) return

    this.updateHiddenTimeFields()
    this.updateHeaderDateTime()
  }

  adjustInitialEndTime() {
    const endHour = this.endHourTarget.value
    const endMinute = this.endMinuteTarget.value

    if (this.endHourTarget.selectedOptions[0].disabled) {
      let newMinute = parseInt(endMinute) - 15
      let newHour = parseInt(endHour)

      if (newMinute < 0) {
        newMinute = 45
        newHour -= 1
      }

      while (newHour >= 8) {
        const hourOption = this.endHourTarget.querySelector(`option[value="${newHour.toString().padStart(2, '0')}"]`)
        if (hourOption && !hourOption.disabled) {
          hourOption.selected = true
          this.endHourTarget.dispatchEvent(new Event('change'))

          const minuteOption = this.endMinuteTarget.querySelector(`option[value="${newMinute.toString().padStart(2, '0')}"]`)
          if (minuteOption && !minuteOption.disabled) {
            minuteOption.selected = true
          } else {
            const firstAvailableMinute = this.endMinuteTarget.querySelector('option:not([disabled])')
            if (firstAvailableMinute) {
              firstAvailableMinute.selected = true
            }
          }
          break
        }
        newMinute = 45
        newHour -= 1
      }
    }
  }

  adjustEndTime(durationMinutes) {
    if (!this.hasStartHourTarget || !this.hasStartMinuteTarget ||
        !this.hasEndHourTarget || !this.hasEndMinuteTarget) return

    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null

    const startHour = parseInt(this.startHourTarget.value)
    const startMinute = parseInt(this.startMinuteTarget.value)

    if (isNaN(startHour) || isNaN(startMinute)) return

    let endHour = startHour
    let endMinute = startMinute + durationMinutes

    while (endMinute >= 60) {
      endHour += 1
      endMinute -= 60
    }

    const endHourStr = endHour.toString().padStart(2, '0')
    const endMinuteStr = endMinute.toString().padStart(2, '0')

    const endHourOption = this.endHourTarget.querySelector(`option[value="${endHourStr}"]`)
    const endMinuteOption = this.endMinuteTarget.querySelector(`option[value="${endMinuteStr}"]`)

    if (endHourOption && !endHourOption.disabled && endMinuteOption && !endMinuteOption.disabled) {
      this.endHourTarget.value = endHourStr
      this.endMinuteTarget.value = endMinuteStr

      this.endHourTarget.dispatchEvent(new Event('change'))
      this.endMinuteTarget.dispatchEvent(new Event('change'))
    } else {
      if (!isEditMode) {
        alert(`Este horario no esta disponible. Hay eventos agendados que impiden programar la cita hasta las ${endHourStr}:${endMinuteStr}hs.`)
      }
      this.findNextAvailableEndTime(startHour, startMinute, durationMinutes)
    }

    this.updateHiddenTimeFields()
    this.updateHeaderDateTime()
  }

  findNextAvailableEndTime(startHour, startMinute, durationMinutes) {
    let currentHour = startHour
    let currentMinute = startMinute + durationMinutes

    while (currentMinute >= 60) {
      currentHour += 1
      currentMinute -= 60
    }

    const hourStr = currentHour.toString().padStart(2, '0')
    const minuteStr = currentMinute.toString().padStart(2, '0')

    this.endHourTarget.value = hourStr
    this.endMinuteTarget.value = minuteStr

    this.endHourTarget.dispatchEvent(new Event('change'))
    this.endMinuteTarget.dispatchEvent(new Event('change'))
  }

  updateStartMinutes() {
    const selectedHourOption = this.startHourTarget.selectedOptions[0]
    const bookedMinutes = JSON.parse(selectedHourOption.dataset.bookedMinutes || '[]')

    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null
    const currentEventId = isEditMode ? document.querySelector('input[name="appointment[event_id]"]').value : null

    // Enable all minutes first
    this.startMinuteTarget.querySelectorAll('option').forEach(option => {
      option.disabled = false
    })

    // Disable booked minutes
    bookedMinutes.forEach(slot => {
      if (!slot.is_end || slot.is_start) {
        if (!isEditMode || slot.event_id !== currentEventId) {
          const minuteOption = this.startMinuteTarget.querySelector(`option[value="${slot.minute.toString().padStart(2, '0')}"]`)
          if (minuteOption) {
            minuteOption.disabled = true
          }
        }
      }
    })

    // Check if all minutes are disabled
    const allMinutesDisabled = Array.from(this.startMinuteTarget.querySelectorAll('option'))
      .filter(option => option.value !== '')
      .every(option => option.disabled)

    if (allMinutesDisabled) {
      selectedHourOption.disabled = true

      if (this.startHourTarget.selectedOptions[0].disabled) {
        const firstAvailableHour = this.startHourTarget.querySelector('option:not([disabled])')
        if (firstAvailableHour) {
          firstAvailableHour.selected = true
          this.startHourTarget.dispatchEvent(new Event('change'))
        }
      }
    } else {
      selectedHourOption.disabled = false
    }

    if (this.startMinuteTarget.selectedOptions[0].disabled) {
      const firstAvailableMinute = this.startMinuteTarget.querySelector('option:not([disabled])')
      if (firstAvailableMinute) {
        firstAvailableMinute.selected = true
      }
    }

    this.validateStartTime()
  }

  updateEndMinutes() {
    const selectedHourOption = this.endHourTarget.selectedOptions[0]
    const bookedMinutes = JSON.parse(selectedHourOption.dataset.bookedMinutes || '[]')

    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null
    const currentEventId = isEditMode ? document.querySelector('input[name="appointment[event_id]"]').value : null

    // Enable all minutes first
    this.endMinuteTarget.querySelectorAll('option').forEach(option => {
      option.disabled = false
    })

    // Disable booked minutes
    bookedMinutes.forEach(slot => {
      if (!slot.is_start || slot.is_end) {
        if (!isEditMode || slot.event_id !== currentEventId) {
          const minuteOption = this.endMinuteTarget.querySelector(`option[value="${slot.minute.toString().padStart(2, '0')}"]`)
          if (minuteOption) {
            minuteOption.disabled = true
          }
        }
      }
    })

    // Check if all minutes are disabled
    const allMinutesDisabled = Array.from(this.endMinuteTarget.querySelectorAll('option'))
      .filter(option => option.value !== '')
      .every(option => option.disabled)

    if (allMinutesDisabled) {
      selectedHourOption.disabled = true

      if (this.endHourTarget.selectedOptions[0].disabled) {
        const firstAvailableHour = this.endHourTarget.querySelector('option:not([disabled])')
        if (firstAvailableHour) {
          firstAvailableHour.selected = true
          this.endHourTarget.dispatchEvent(new Event('change'))
        }
      }
    } else {
      selectedHourOption.disabled = false
    }

    if (this.endMinuteTarget.selectedOptions[0].disabled) {
      const firstAvailableMinute = this.endMinuteTarget.querySelector('option:not([disabled])')
      if (firstAvailableMinute) {
        firstAvailableMinute.selected = true
      }
    }

    this.validateEndTime()
  }

  // ==============================================
  // LOCATION HANDLING
  // ==============================================

  updateLocation(event) {
    const locationType = event.target.dataset.location
    const meetLinkInput = this.hasMeetLinkInputTarget ? this.meetLinkInputTarget : document.querySelector('input[name="appointment[meet_link]"]')
    const addressField = document.getElementById('addressField')
    const addressInput = addressField?.querySelector('input')
    const meetLinkField = document.getElementById('meetLinkField')

    const originalMeetLink = meetLinkInput?.value || ''
    const hasOriginalMeetLink = originalMeetLink.includes('meet.google.com')

    const defaultAddress = addressInput?.dataset?.defaultAddress || 'Direccion no configurada'
    const originalAddress = addressInput?.dataset?.originalAddress || defaultAddress

    if (locationType === 'Virtual') {
      if (addressField) {
        addressField.style.display = 'none'
        if (addressInput) {
          addressInput.value = ''
          addressInput.required = false
          addressInput.disabled = true
        }
      }

      if (meetLinkField) {
        meetLinkField.style.display = 'block'
      }

      const meetLinkText = meetLinkField?.querySelector('#meetLinkText')

      if (meetLinkText) {
        if (hasOriginalMeetLink) {
          // Sanitize meet link to prevent XSS
          const link = document.createElement('a')
          link.href = originalMeetLink
          link.target = '_blank'
          link.textContent = originalMeetLink
          meetLinkText.replaceChildren(link)
          meetLinkInput.value = originalMeetLink
        } else {
          meetLinkText.textContent = 'El link de Google Meet se creara automaticamente al guardar la cita'
          meetLinkInput.value = 'virtual_appointment'
        }
      }
    } else {
      // Presencial
      if (addressField) {
        addressField.style.display = 'block'
        if (addressInput) {
          addressInput.value = originalAddress
          addressInput.required = true
          addressInput.disabled = false
        }
      }

      if (meetLinkField) {
        meetLinkField.style.display = 'none'
      }

      if (meetLinkInput) {
        if (!hasOriginalMeetLink) {
          meetLinkInput.value = ''
        }
      }
    }

    this.validateAddress()
  }

  validateAddress() {
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')

    if (!this.hasAddressFieldTarget) {
      return
    }

    const addressInput = this.addressFieldTarget

    if (locationInput && locationInput.value === 'Presencial') {
      if (!addressInput.value || addressInput.value.trim() === '') {
        addressInput.setCustomValidity('La direccion es requerida para citas presenciales')
      } else {
        addressInput.setCustomValidity('')
      }
    } else {
      addressInput.setCustomValidity('')
    }
  }

  generateMeetLink() {
    const meetingId = Math.random().toString(36).substring(2, 15)
    return `https://meet.google.com/${meetingId}`
  }

  // ==============================================
  // DATE HANDLING
  // ==============================================

  validateDate(event) {
    const blockedDates = JSON.parse(this.outOfOfficeDatesTarget.value || '[]')

    // El input date ya trae "YYYY-MM-DD": no pasarlo por new Date() y toISOString(),
    // que con el campo vacío tira RangeError.
    const selectedDateStr = event.target.value
    const isBlocked = blockedDates.some(date => date === selectedDateStr)

    if (isBlocked) {
      event.preventDefault()
      event.target.value = event.target.dataset.originalValue

      const [year, month, day] = event.target.value.split('-').map(Number)
      const dateForWeekday = new Date(year, month - 1, day)
      const dayOfWeek = dateForWeekday.getDay()

      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

      if (isWeekend) {
        alert('No se pueden programar citas los sabados y domingos.')
      } else {
        alert('No se pueden programar citas en dias que tienen eventos de todo el dia.')
      }
      return
    }

    this.updateHeaderDateTime()
  }

  updateHeaderDateTime() {
    if (!this.hasDateFieldTarget || !this.hasStartHourTarget || !this.hasStartMinuteTarget || !this.hasHeaderDateTimeTarget) {
      return
    }

    const date = this.dateFieldTarget.value
    const startHour = this.startHourTarget.value
    const startMinute = this.startMinuteTarget.value

    if (date && startHour && startMinute) {
      const [year, month, dayOfMonth] = date.split('-').map(Number)
      const dateObj = new Date(year, month - 1, dayOfMonth)
      const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado']
      const dayName = dayNames[dateObj.getDay()]
      const day = dateObj.getDate()
      const time = `${startHour}:${startMinute}`

      this.headerDateTimeTarget.textContent = `${dayName} ${day} - ${time}hs`
    }
  }

  initializeDatePickerValidation() {
    if (this.hasDateFieldTarget) {
      const dateField = this.dateFieldTarget

      dateField.addEventListener('input', (e) => {
        this.validateDateInput(e.target.value)
      })
    }
  }

  validateDateInput(dateValue) {
    if (!dateValue) return

    const blockedDates = JSON.parse(this.outOfOfficeDatesTarget.value || '[]')
    const isBlocked = blockedDates.some(date => date === dateValue)

    if (isBlocked) {
      const [year, month, day] = dateValue.split('-').map(Number)
      const dateForWeekday = new Date(year, month - 1, day)
      const dayOfWeek = dateForWeekday.getDay()

      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

      if (isWeekend) {
        alert('No se pueden programar citas los sabados y domingos.')
      } else {
        alert('No se pueden programar citas en dias que tienen eventos de todo el dia.')
      }

      this.dateFieldTarget.value = this.dateFieldTarget.dataset.originalValue
    }
  }

  // ==============================================
  // FORM VALIDATION
  // ==============================================

  validateForm(event) {
    const startTime = this.startTimeTarget.value
    const endTime = this.endTimeTarget.value
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked') || document.querySelector('input[name="location"]:checked')
    const addressInput = this.hasAddressFieldTarget ? this.addressFieldTarget : null
    const isEditable = !document.querySelector('select[name="start_hour"]').disabled

    const isEditMode = document.querySelector('input[name="appointment[event_id]"]') !== null

    const isGeneratingInvoice = event.submitter && (
      event.submitter.classList.contains('btn-invoice-afip') ||
      event.submitter.textContent.includes('Generar factura AFIP') ||
      event.submitter.getAttribute('data-action')?.includes('afip-invoice')
    )

    if (!isEditable) {
      return true
    }

    if (!startTime || !endTime) {
      alert('Por favor, complete ambos campos de hora')
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    if (isEditable && locationInput && locationInput.value === 'Presencial') {
      if (!addressInput || !addressInput.value || addressInput.value.trim() === '') {
        alert('Por favor, ingrese la direccion para la cita presencial')
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    if (isGeneratingInvoice) {
      const baseDescription = document.querySelector('textarea[name="appointment[base_description]"]')
      const diagnosisHealthInsurance = document.querySelector('textarea[name="appointment[diagnosis_health_insurance]"]')

      if (baseDescription && (!baseDescription.value || baseDescription.value.trim() === '')) {
        alert('Por favor, complete la descripcion de la consulta para generar la factura AFIP')
        event.preventDefault()
        event.stopPropagation()
        return false
      }

      if (diagnosisHealthInsurance && (!diagnosisHealthInsurance.value || diagnosisHealthInsurance.value.trim() === '')) {
        alert('Por favor, complete el diagnostico y prepaga para generar la factura AFIP')
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    }

    const [startHours, startMinutes] = startTime.split(':').map(Number)
    const [endHours, endMinutes] = endTime.split(':').map(Number)

    const startTotalMinutes = startHours * 60 + startMinutes
    const endTotalMinutes = endHours * 60 + endMinutes

    let hasError = false

    if (startTotalMinutes < 8 * 60 || startTotalMinutes > 24 * 60) {
      alert('Las horas disponibles son de 8:00 a 24:00')
      hasError = true
    }

    if (endTotalMinutes < 8 * 60 || endTotalMinutes > 24 * 60) {
      alert('Las horas disponibles son de 8:00 a 24:00')
      hasError = true
    }

    if (startTotalMinutes >= endTotalMinutes) {
      alert('La hora de inicio debe ser anterior a la hora de fin')
      hasError = true
    }

    if (!isEditMode) {
      const startHourOption = this.startHourTarget.selectedOptions[0]
      const endHourOption = this.endHourTarget.selectedOptions[0]
      const startBookedMinutes = JSON.parse(startHourOption.dataset.bookedMinutes || '[]')
      const endBookedMinutes = JSON.parse(endHourOption.dataset.bookedMinutes || '[]')

      const startTimeOccupied = startBookedMinutes.some(slot =>
        slot.minute === startMinutes && (!slot.is_end || slot.is_start)
      )

      const endTimeOccupied = endBookedMinutes.some(slot =>
        slot.minute === endMinutes && (!slot.is_start || slot.is_end)
      )

      let hasConflictInMiddle = false
      let currentHour = startHours
      let currentMinute = startMinutes

      while (currentHour < endHours || (currentHour === endHours && currentMinute < endMinutes)) {
        const hourOption = this.startHourTarget.querySelector(`option[value="${currentHour.toString().padStart(2, '0')}"]`)
        if (hourOption) {
          const bookedMinutes = JSON.parse(hourOption.dataset.bookedMinutes || '[]')
          const hasConflict = bookedMinutes.some(slot => {
            const slotTotalMinutes = currentHour * 60 + slot.minute
            return slotTotalMinutes > startTotalMinutes &&
                   slotTotalMinutes < endTotalMinutes &&
                   (!slot.is_end || slot.is_start)
          })

          if (hasConflict) {
            hasConflictInMiddle = true
            break
          }
        }

        currentMinute += 15
        if (currentMinute >= 60) {
          currentMinute = 0
          currentHour += 1
        }
      }

      if (startTimeOccupied) {
        alert('La hora de inicio ya se encuentra ocupada. Por favor elija otra hora.')
        hasError = true
      }

      if (endTimeOccupied) {
        alert('La hora de fin ya se encuentra ocupada. Por favor elija otra hora.')
        hasError = true
      }

      if (hasConflictInMiddle) {
        alert('El rango de tiempo seleccionado incluye horas que ya estan ocupadas. Por favor elija otro rango.')
        hasError = true
      }
    }

    if (hasError) {
      if (this.startTimeTarget.dataset.originalTime) {
        const [originalHour, originalMinute] = this.startTimeTarget.dataset.originalTime.split(':')
        const hourOption = this.startHourTarget.querySelector(`option[value="${originalHour}"]`)
        if (hourOption && !hourOption.disabled) {
          this.startHourTarget.value = originalHour
          this.startMinuteTarget.value = originalMinute
        } else {
          const firstAvailableHour = this.startHourTarget.querySelector('option:not([disabled])')
          if (firstAvailableHour) {
            firstAvailableHour.selected = true
            this.startHourTarget.dispatchEvent(new Event('change'))
          }
        }
      }

      if (this.endTimeTarget.dataset.originalTime) {
        const [originalHour, originalMinute] = this.endTimeTarget.dataset.originalTime.split(':')
        const hourOption = this.endHourTarget.querySelector(`option[value="${originalHour}"]`)
        if (hourOption && !hourOption.disabled) {
          this.endHourTarget.value = originalHour
          this.endMinuteTarget.value = originalMinute
        } else {
          const firstAvailableHour = this.endHourTarget.querySelector('option:not([disabled])')
          if (firstAvailableHour) {
            firstAvailableHour.selected = true
            this.endHourTarget.dispatchEvent(new Event('change'))
          }
        }
      }

      this.updateHiddenTimeFields()

      event.preventDefault()
      event.stopPropagation()
      return false
    }

    return true
  }

  // ==============================================
  // FILE HANDLING
  // ==============================================

  validateFileSize(event) {
    const file = event.target.files[0]
    const maxSizeMB = parseInt(event.target.dataset.maxSize) || 10
    const maxSizeBytes = maxSizeMB * 1024 * 1024

    if (file && file.size > maxSizeBytes) {
      alert(`El archivo es demasiado grande. El tamano maximo permitido es ${maxSizeMB}MB.`)
      event.target.value = ''
      return false
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    if (file && !allowedTypes.includes(file.type)) {
      alert('Formato de archivo no valido. Solo se permiten archivos JPG, PNG y PDF.')
      event.target.value = ''
      return false
    }

    return true
  }

  // ==============================================
  // COLLAPSIBLE SECTIONS
  // ==============================================

  toggleIcon(event) {
    const button = event.currentTarget
    const targetId = button.getAttribute('data-target')

    let icon = null
    if (targetId === '#moreInfo') {
      icon = this.element.querySelector('[data-appointment-form-target="moreInfoIcon"]')
    } else if (targetId === '#paymentBilling') {
      icon = this.element.querySelector('[data-appointment-form-target="paymentBillingIcon"]')
    }

    if (icon) {
      const isCurrentlyPlus = icon.classList.contains('fa-plus-circle')
      const isCurrentlyChevronDown = icon.classList.contains('fa-chevron-down')

      if (isCurrentlyPlus) {
        icon.classList.remove('fa-plus-circle')
        icon.classList.add('fa-minus-circle')
      } else if (isCurrentlyChevronDown) {
        icon.classList.remove('fa-chevron-down')
        icon.classList.add('fa-chevron-up')
      } else if (icon.classList.contains('fa-minus-circle')) {
        icon.classList.remove('fa-minus-circle')
        icon.classList.add('fa-plus-circle')
      } else if (icon.classList.contains('fa-chevron-up')) {
        icon.classList.remove('fa-chevron-up')
        icon.classList.add('fa-chevron-down')
      }
    }
  }

  toggleReceiptField(event) {
    const paymentMethod = event.target.value
    const receiptSection = document.getElementById('receiptSection')

    if (receiptSection) {
      if (paymentMethod === 'transferencia') {
        receiptSection.style.display = 'block'
        receiptSection.style.marginBottom = '1rem'
      } else {
        receiptSection.style.display = 'none'
        receiptSection.style.marginBottom = '0'
        const fileInput = receiptSection.querySelector('input[type="file"]')
        if (fileInput) {
          fileInput.value = ''
        }
      }
    }
  }

  toggleInvoiceField(event) {
    const isBilled = event.target.checked
    const invoiceSection = document.getElementById('invoiceSection')

    if (invoiceSection) {
      if (isBilled) {
        invoiceSection.style.display = 'block'
      } else {
        invoiceSection.style.display = 'none'
        const fileInput = invoiceSection.querySelector('input[type="file"]')
        if (fileInput) {
          fileInput.value = ''
        }
      }
    }
  }

  initializeCollapsibleIcons() {
    ['#moreInfo', '#paymentBilling'].forEach(collapseId => {
      const collapseElement = this.element.querySelector(collapseId)
      if (collapseElement) {
        const button = this.element.querySelector(`[data-target="${collapseId}"]`)
        const icon = button ? button.querySelector('i') : null

        if (icon) {
          collapseElement.addEventListener('show.bs.collapse', () => {
            icon.classList.remove('fa-plus-circle')
            icon.classList.add('fa-minus-circle')
          })

          collapseElement.addEventListener('hide.bs.collapse', () => {
            icon.classList.remove('fa-minus-circle')
            icon.classList.add('fa-plus-circle')
          })

          if (collapseElement.classList.contains('show')) {
            icon.classList.remove('fa-plus-circle')
            icon.classList.add('fa-minus-circle')
          } else {
            icon.classList.remove('fa-minus-circle')
            icon.classList.add('fa-plus-circle')
          }
        }
      }
    })
  }

  toggleReceiptUpload(event) {
    event.preventDefault()
    const uploadSection = document.getElementById('receiptUploadSection')
    if (uploadSection) {
      uploadSection.style.display = uploadSection.style.display === 'none' ? 'flex' : 'none'
      uploadSection.style.marginTop = uploadSection.style.display === 'none' ? '0' : '1rem'
      uploadSection.style.marginBottom = uploadSection.style.display === 'none' ? '0' : '1rem'
    }
  }

  toggleInvoiceUpload(event) {
    event.preventDefault()
    const uploadSection = document.getElementById('invoiceUploadSection')
    if (uploadSection) {
      uploadSection.style.display = uploadSection.style.display === 'none' ? 'flex' : 'none'
      uploadSection.style.marginTop = uploadSection.style.display === 'none' ? '0' : '1rem'
      uploadSection.style.marginBottom = uploadSection.style.display === 'none' ? '0' : '1rem'
    }
  }

  initializeBootstrapCollapse() {
    const collapsibleButtons = document.querySelectorAll('[data-toggle="collapse"]')
    collapsibleButtons.forEach(button => {
      const targetId = button.getAttribute('data-target')
      const isExpanded = button.getAttribute('aria-expanded') === 'true'

      let content = null
      if (targetId === '#moreInfo') {
        content = document.getElementById('moreInfo')
      } else if (targetId === '#paymentBilling') {
        content = document.getElementById('paymentBilling')
      }

      if (content) {
        if (isExpanded) {
          content.style.display = 'block'
        } else {
          content.style.display = 'none'
        }
      }
    })
  }

  initializeLocationFields() {
    const locationInput = document.querySelector('input[name="appointment[location]"]:checked')
    if (locationInput) {
      try {
        this.updateLocation({ target: { dataset: { location: locationInput.value } } })
      } catch (error) {
        console.warn('Error initializing location fields:', error)
      }
    }
  }

  // ==============================================
  // PRICE HANDLING
  // ==============================================

  initializePriceHandling() {
    this.priceTypeRadios = document.querySelectorAll('input[name="price_type"]')
    this.manualPriceField = document.getElementById('manual_price_field')

    if (this.priceTypeRadios.length > 0) {
      const checkedRadio = document.querySelector('input[name="price_type"]:checked')
      if (checkedRadio) {
        this.handlePriceTypeChange({ target: checkedRadio })
      }

      this.priceTypeRadios.forEach(radio => {
        radio.addEventListener('change', this.handlePriceTypeChange.bind(this))
      })
    }

    if (this.hasPatientIdTarget) {
      this.patientIdTarget.addEventListener('change', this.updateCalculatedPriceDisplay.bind(this))
    }
  }

  handlePriceTypeChange(event) {
    const useAutomaticField = document.getElementById('use_automatic_price_field')
    const useSavedAutomaticField = document.getElementById('use_saved_automatic_price_field')

    if (event.target.value === 'manual') {
      this.manualPriceField.style.display = 'block'
      if (useAutomaticField) useAutomaticField.value = 'false'
      if (useSavedAutomaticField) useSavedAutomaticField.value = 'false'

      const priceInput = this.manualPriceField.querySelector('input[type="number"]')
      if (priceInput) {
        setTimeout(() => priceInput.focus(), 100)
      }
    } else if (event.target.value === 'saved_automatic') {
      this.manualPriceField.style.display = 'none'
      if (useAutomaticField) useAutomaticField.value = 'false'
      if (useSavedAutomaticField) useSavedAutomaticField.value = 'true'
    } else {
      // 'automatic' - use current automatic price
      this.manualPriceField.style.display = 'none'
      if (useAutomaticField) useAutomaticField.value = 'true'
      if (useSavedAutomaticField) useSavedAutomaticField.value = 'false'
    }
  }

  updateCalculatedPriceDisplay() {
    const doctorId = this.data.get('doctor-id')
    const patientId = this.patientIdTarget?.value

    if (doctorId && patientId) {
      fetch(`/appointments/consultation_price_info?doctor_id=${doctorId}&patient_id=${patientId}`, {
        signal: this.abortController.signal
      })
        .then(response => response.json())
        .then(data => {
          const displayElement = document.querySelector('[data-appointment-form-target="calculatedPriceDisplay"]')
          if (displayElement) {
            if (data.price && data.price > 0) {
              displayElement.textContent = `$${Math.round(data.price)}`
              displayElement.className = ''
            } else {
              displayElement.textContent = 'El doctor debe configurar los precios'
              displayElement.className = 'text-danger'
            }
          }
        })
        .catch(error => {
          console.error('Error al obtener precio calculado:', error)
          const displayElement = document.querySelector('[data-appointment-form-target="calculatedPriceDisplay"]')
          if (displayElement) {
            displayElement.textContent = 'Error al calcular precio'
            displayElement.className = 'text-danger'
          }
        })
    }
  }
}
